import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";
import { Search, ArrowRight, Calendar, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const [blogsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/blogs`),
          axios.get(`${API}/blogs/categories/list`),
        ]);
        setBlogs(blogsRes.data);
        setCategories(categoriesRes.data.categories || []);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      search === "" ||
      blog.title.toLowerCase().includes(search.toLowerCase()) ||
      blog.excerpt.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <SEO
        title="Insights on AI Governance & MedTech Compliance | Saloni Shah"
        description="Expert insights on AI governance, FDA regulations, EU AI Act, SaMD compliance, and MedTech regulatory strategy. Stay updated with the latest in regulatory AI."
        url="/blog"
        keywords={[
          "AI governance blog",
          "MedTech compliance articles",
          "FDA regulations insights",
          "EU AI Act guide",
          "SaMD compliance tips",
        ]}
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-secondary via-neutral-50 to-neutral-100" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center animate-fade-in-up">
            <h1 className="font-serif font-bold text-5xl md:text-6xl text-neutral-900 mb-4">
              Insights
            </h1>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Thoughts on AI governance, regulatory strategy, and navigating
              compliance in the evolving MedTech landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-white border-b border-neutral-100 sticky top-20 z-40" data-testid="blog-filters">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-neutral-200 rounded-full focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-neutral-50"
                data-testid="blog-search"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 justify-center">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedCategory === "all"
                    ? "bg-primary text-white"
                    : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                }`}
                data-testid="filter-all"
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? "bg-primary text-white"
                      : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                  }`}
                  data-testid={`filter-${category.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 bg-neutral-50" data-testid="blog-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl p-6 animate-pulse"
                >
                  <div className="h-48 bg-neutral-200 rounded-lg mb-4" />
                  <div className="h-4 bg-neutral-200 rounded w-1/4 mb-4" />
                  <div className="h-6 bg-neutral-200 rounded mb-2" />
                  <div className="h-6 bg-neutral-200 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-neutral-200 rounded w-full mb-2" />
                  <div className="h-4 bg-neutral-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-neutral-600 text-lg mb-4">
                No articles found matching your criteria.
              </p>
              <button
                onClick={() => {
                  setSearch("");
                  setSelectedCategory("all");
                }}
                className="text-primary hover:text-primary-light font-medium"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog, index) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.slug}`}
                  className="bg-white rounded-xl overflow-hidden border border-neutral-100 hover:border-primary/20 hover:shadow-xl transition-all duration-300 group animate-fade-in-up flex flex-col"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`blog-card-${blog.slug}`}
                >
                  {blog.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={blog.featured_image}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-medium text-primary uppercase tracking-wider bg-primary/10 px-2 py-1 rounded">
                        {blog.category}
                      </span>
                    </div>
                    <h2 className="font-serif font-semibold text-xl text-neutral-900 mb-3 group-hover:text-primary transition-colors duration-300 line-clamp-2">
                      {blog.title}
                    </h2>
                    <p className="text-neutral-600 text-sm line-clamp-3 mb-4 flex-1">
                      {blog.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-neutral-500 pt-4 border-t border-neutral-100">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(blog.created_at)}
                        </span>
                      </div>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {blog.read_time}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary" data-testid="blog-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif font-semibold text-3xl text-white mb-4 animate-fade-in-up">
            Want to Discuss These Topics?
          </h2>
          <p className="text-white/80 mb-8 animate-fade-in-up animation-delay-100">
            I'm always open to conversations about AI governance and MedTech
            compliance challenges.
          </p>
          <a
            href="https://www.linkedin.com/in/saloni-s-246823188"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-primary hover:bg-neutral-100 rounded-full px-8 py-4 font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 animate-fade-in-up animation-delay-200"
            data-testid="blog-cta-connect"
          >
            Connect on LinkedIn
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </>
  );
}
